import type {
  AddCohortMembershipsArgs,
  AdminCode,
  AdminCohort,
  AdminCohortCoach,
  AdminRoster,
  CohortCoachArgs,
  BatchMoveResult,
  BatchMoveSeatsArgs,
  BatchSeatResult,
  CapacityLine,
  CreateCohortArgs,
  ErrorEnvelope,
  MintCodeArgs,
  MintedCode,
  MatchedInstitution,
  MoveDestination,
  MoveDestinationGroup,
  MovedSeat,
  MoveSeatArgs,
  NamedRef,
  OperatorContextRead,
  OperatorCoverage,
  OperatorFleetArgs,
  OperatorRow,
  RenameCohortArgs,
  RenamedCohort,
  RevokedSeat,
  RosterCapMode,
  RosterSeat,
  SeatOccupant,
} from './institutionAdmin.types';
import {
  decodeOperatorContextRefusal,
  OPERATOR_CONTEXT_HEADER,
  type HeldRoot,
} from '../operatorContext/operatorContext.contract';

export const INSTITUTION_ADMIN_ROUTE = {
  rosters: 'institution-admin/rosters',
  /** Spans operators rather than resolving one, so it ignores the operator-context header. */
  operators: 'institution-admin/operators',
  operatorContext: 'institution-admin/operator-context',
  /** Lists on GET, creates on POST. */
  cohorts: (rosterId: number) => `institution-admin/rosters/${rosterId}/cohorts`,
  /** Renames on PATCH. */
  cohort: (rosterId: number, cohortId: number) =>
    `institution-admin/rosters/${rosterId}/cohorts/${cohortId}`,
  /** Attaches on POST. */
  cohortCoaches: (rosterId: number, cohortId: number) =>
    `institution-admin/rosters/${rosterId}/cohorts/${cohortId}/coaches`,
  /** Detaches on DELETE. */
  cohortCoach: (rosterId: number, cohortId: number, coachUserId: number) =>
    `institution-admin/rosters/${rosterId}/cohorts/${cohortId}/coaches/${coachUserId}`,
  /** Lists on GET, mints on POST. */
  codes: (rosterId: number) => `institution-admin/rosters/${rosterId}/codes`,
  revokeCode: (rosterId: number, codeId: number) =>
    `institution-admin/rosters/${rosterId}/codes/${codeId}/revoke`,
  seats: (rosterId: number) => `institution-admin/rosters/${rosterId}/seats`,
  revokeSeat: (rosterId: number, seatId: number) =>
    `institution-admin/rosters/${rosterId}/seats/${seatId}/revoke`,
  moveSeat: (rosterId: number, seatId: number) =>
    `institution-admin/rosters/${rosterId}/seats/${seatId}/move`,
  /** Full manage reach rather than the resolved operator context: a move is a revoke
   * under one operator and an add under another, so both ends are the caller's to pick. */
  moveDestinations: 'institution-admin/move-destinations',
  /** Adds on POST — a batch, never a single. */
  cohortMemberships: (rosterId: number, cohortId: number) =>
    `institution-admin/rosters/${rosterId}/cohorts/${cohortId}/memberships`,
  /** Removes on POST — a batch, mirroring the add door. */
  removeCohortMemberships: (rosterId: number, cohortId: number) =>
    `institution-admin/rosters/${rosterId}/cohorts/${cohortId}/memberships/remove`,
  /** Not roster-nested: one batch may carry seats from several source rosters. */
  batchMoveSeats: 'institution-admin/seats/batch-move',
} as const;

export const REQUEST_PARAM = {
  filter: 'filter',
  institutionIds: 'institution_ids',
  seatId: 'seat_id',
  range: 'range',
  search: 'search',
  sort: 'sort',
} as const;

/** Narrows the paged seat read to the one seat, which paging alone cannot reach. */
export const encodeSeatIdFilter = (seatId: number): string =>
  JSON.stringify({ [REQUEST_PARAM.seatId]: seatId });

/** a comma-joined string is dropped without an error, leaving the read unfiltered */
export const encodeInstitutionIdsFilter = (institutionIds: readonly number[]): string =>
  JSON.stringify({ [REQUEST_PARAM.institutionIds]: [...institutionIds] });

export const REQUEST_BODY = {
  cohortName: 'name',
  startDate: 'start_date',
  endDate: 'end_date',
  cohortId: 'cohort_id',
  code: 'code',
  coachUserId: 'coach_user_id',
  coachUserIds: 'coach_user_ids',
  destinationRosterId: 'destination_roster_id',
  sourceGrantId: 'source_grant_id',
  rosterSeatIds: 'roster_seat_ids',
  seatIds: 'seat_ids',
} as const;

/**
 * The one rule every cohort-name door submits under. Shared rather than repeated so the
 * two dialogs cannot drift into accepting different names for the same field.
 */
export const isSubmittableCohortName = (name: string): boolean => name.trim() !== '';

/** The shape the API mints and accepts: six uppercase letters or digits. */
export const CODE_LENGTH = 6;
export const CODE_FORMAT = /^[A-Z0-9]{6}$/;
export const isSubmittableCode = (code: string): boolean => CODE_FORMAT.test(code);
/** Keeps a typed draft within the shape: uppercased, code characters only, at most six. */
export const normalizeCodeDraft = (draft: string): string =>
  draft
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, CODE_LENGTH);

const CODE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** A random code in the API's shape, drawn from the browser's CSPRNG. */
export const randomCode = (): string =>
  Array.from(
    crypto.getRandomValues(new Uint8Array(CODE_LENGTH)),
    (byte) => CODE_ALPHABET[byte % CODE_ALPHABET.length],
  ).join('');

/** Marks a mint that handed back an already-active code instead of inserting. */
export const RECYCLED_FIELD = 'recycled';

export const RESPONSE_HEADER = {
  operatorInstitutionId: OPERATOR_CONTEXT_HEADER,
  totalCount: 'X-Total-Count',
} as const;

export {
  decodeOperatorContextRefusal,
  OPERATOR_CONTEXT_REQUIRED,
} from '../operatorContext/operatorContext.contract';

export const INSTITUTION_OUTSIDE_OPERATOR_CONTEXT = 'institution_outside_operator_context';

/** The first refusal that offers roots to choose between, or null where none does. */
export const firstOperatorContextRefusal = (errors: readonly unknown[]): HeldRoot[] | null => {
  for (const error of errors) {
    const roots = decodeOperatorContextRefusal(error);

    if (roots !== null) return roots;
  }

  return null;
};

/** Both write doors refuse with this when a roster has more than one mintable line. */
export const MINT_LINE_AMBIGUOUS = 'mint_line_ambiguous';
/** A caller-chosen code the API refused because another invite code already holds it. */
export const INVITE_CODE_TAKEN = 'invite_code_taken';
/** A code was chosen for a cohort whose roster has no line that can issue one. */
export const CODE_NEEDS_MINT_LINE = 'code_needs_mint_line';

/**
 * Client-minted, never sent by the server: a 2xx is post-commit, so a write whose
 * response will not decode has already landed and must not be reported as one that
 * did not happen.
 */
export const WRITE_UNREADABLE = 'write_unreadable';

/**
 * Reasons meaning "the write COMMITTED and could not be read back". The first is minted here on
 * an undecodable 2xx; the other two are the server's, which reaches for them precisely so the
 * operator is not told a landed write was denied. Rendering any of them as a refusal produces the
 * misleading sequence they exist to prevent.
 */
export const LANDED_UNREADABLE_CODES: readonly string[] = [
  WRITE_UNREADABLE,
  'revoked_code_unreadable',
  'minted_code_unreadable',
];

/**
 * Whether a rejected write actually COMMITTED and only its readback failed. Reachable
 * only past a 2xx, so answering it as a refusal tells an operator a destructive write did
 * not happen — and the retry it invites then refuses for a second, unrelated reason.
 */
export const isLandedUnreadable = (error: unknown): boolean => {
  const code = decodeErrorEnvelope(error)?.code;

  return code !== undefined && code !== null && LANDED_UNREADABLE_CODES.includes(code);
};

const ERROR_ENVELOPE = {
  code: 'code',
  prose: 'error',
} as const;

const CAP_MODES: readonly RosterCapMode[] = ['hard', 'soft', 'none'];

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const nullableNum = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const nullableStr = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const blankToNull = (value: unknown): string | null => {
  const text = nullableStr(value);

  return text !== null && text.trim() !== '' ? text : null;
};

const rowId = (value: unknown): number | null => {
  const parsed = nullableNum(value);

  return parsed !== null && parsed > 0 ? parsed : null;
};

const bool = (value: unknown): boolean => value === true;

const namedRef = (value: unknown): NamedRef => {
  const ref = asRecord(value);

  return { id: rowId(ref?.id), name: blankToNull(ref?.name) };
};

const nullableNamedRef = (value: unknown): NamedRef | null =>
  asRecord(value) === null ? null : namedRef(value);

/** A ref that has to be addressable — one carrying no id names nothing that can be acted on. */
const identifiedRef = (value: unknown): NamedRef | null => {
  const ref = namedRef(value);

  return ref.id === null ? null : ref;
};

/**
 * An unrecognised posture falls to the STRICTEST reading, matching the emitting half: under-
 * stating a cap would licence seating the server would refuse
 */
export const isKnownCapMode = (value: unknown): boolean => CAP_MODES.some((mode) => mode === value);

const capMode = (value: unknown): RosterCapMode =>
  CAP_MODES.find((mode) => mode === value) ?? 'hard';

export const decodeRoster = (value: unknown): AdminRoster | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    managingInstitution: namedRef(row.managing_institution),
    offering: {
      ...namedRef(row.offering),
      kind: blankToNull(asRecord(row.offering)?.kind),
    },
    capacity: nullableNum(row.capacity),
    seatsUsed: num(row.seats_used),
    capMode: capMode(row.cap_mode),
    isOverCap: bool(row.over_cap),
    sourceLineCount: num(row.source_line_count),
  };
};

export const decodeList = <T>(
  value: unknown,
  decode: (row: unknown) => T | null,
): { rows: T[]; dropped: number } | null => {
  if (!Array.isArray(value)) return null;

  const rows = value.map(decode).filter((row): row is T => row !== null);

  return { rows, dropped: value.length - rows.length };
};

export const decodeOperatorCoverage = (value: unknown): OperatorCoverage | null => {
  const row = asRecord(value);
  const offeringKind = blankToNull(row?.offering_kind);

  // The kind is the line's identity, as the id is a matched institution's: a line that
  // names no kind reports usage against nothing.
  if (row === null || offeringKind === null) return null;

  return {
    offeringKind,
    capacity: nullableNum(row.capacity),
    usage: num(row.usage),
    isOver: bool(row.over),
  };
};

export const decodeMatchedInstitution = (value: unknown): MatchedInstitution | null => {
  const ref = identifiedRef(value);

  return ref === null || ref.id === null ? null : { id: ref.id, name: ref.name };
};

/** A home that will not decode reads as none: the home row is hidden rather than naming an
 * institution the read did not. An api that does not answer `mode` or `platform_available`
 * reads as an ordinary operator context with no platform mode on offer. */
export const decodeOperatorContextRead = (value: unknown): OperatorContextRead | null => {
  const body = Array.isArray(value) ? null : asRecord(value);

  if (body === null) return null;

  return {
    root: decodeMatchedInstitution(body.root),
    home: decodeMatchedInstitution(body.home),
    mode: body.mode === 'platform' ? 'platform' : 'operator',
    platformAvailable: body.platform_available === true,
  };
};

/** Rejects on a dropped line for the reason `decodeAuthorizedCohortCoaches` does: the loss
 * runs in the understating direction, and unreadable is honest where a short list is not. */
export const decodeOperatorCoverageList = (value: unknown): OperatorCoverage[] | null => {
  const decoded = decodeList(value, decodeOperatorCoverage);

  return decoded === null || decoded.dropped > 0 ? null : decoded.rows;
};

export const decodeOperatorRow = (value: unknown): OperatorRow | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    name: blankToNull(row.name),
    type: blankToNull(row.type),
    status: blankToNull(row.status),
    activeAgreements: num(row.active_agreements),
    admins: num(row.admins),
    rosters: num(row.rosters),
    overCap: num(row.over_cap),
    lapsingSoon: num(row.lapsing_soon),
    lapse: num(row.lapse),
    coverage: decodeOperatorCoverageList(row.coverage),
    // A match that will not decode is dropped rather than taking the operator with it; the
    // list then offers one fewer school than the search reached.
    matchedInstitutions: decodeList(row.matched_institutions, decodeMatchedInstitution)?.rows ?? [],
  };
};

/**
 * The search term is sent as typed. Trimming it to the length the server accepts would
 * answer a different search than the one asked for, and read as a result rather than a
 * refusal.
 */
export const encodeOperatorFleetQuery = ({ search, range, sort }: OperatorFleetArgs): string => {
  const params = new URLSearchParams();

  params.append(REQUEST_PARAM.range, JSON.stringify([range[0], range[1]]));
  params.append(REQUEST_PARAM.sort, JSON.stringify([sort.field, sort.direction]));

  const trimmed = search.trim();

  if (trimmed !== '') {
    params.append(REQUEST_PARAM.filter, JSON.stringify({ [REQUEST_PARAM.search]: trimmed }));
  }

  return params.toString();
};

export const decodeCohortCoach = (value: unknown): AdminCohortCoach | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    firstName: blankToNull(row.first_name),
    lastName: blankToNull(row.last_name),
    profileImageUrl: blankToNull(row.profile_image_url),
  };
};

export const decodeCohort = (value: unknown): AdminCohort | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    name: blankToNull(row.name),
    status: blankToNull(row.status),
    startDate: blankToNull(row.start_date),
    endDate: blankToNull(row.end_date),
    memberCount: num(row.member_count),
    coachCount: num(row.coach_count),
    // An undecodable coach is dropped rather than taking the whole row with it. The shortfall
    // against `coachCount` reaches the console only — no surface renders the count.
    coaches: decodeList(row.coaches, decodeCohortCoach)?.rows ?? [],
    shareCode: blankToNull(row.share_code),
    shareCodeCount: nullableNum(row.share_code_count),
  };
};

export const decodeCode = (value: unknown): AdminCode | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    value: blankToNull(row.value),
    status: blankToNull(row.status),
    cohort: nullableNamedRef(row.cohort),
    isReserved: bool(row.is_reserved),
    hasReservedEmail: bool(row.has_reserved_email),
  };
};

/**
 * Whose seat makes them someone to coach. Mirrored from the server, which stays the
 * authority: the client holds it only to withhold a door that would be accepted and
 * confer nothing.
 */
const COACHABLE_OFFERING_KIND = 'student';

export const isCoachableRoster = (roster: AdminRoster): boolean =>
  roster.offering.kind === COACHABLE_OFFERING_KIND;

/**
 * The only cohort status the ADD door accepts — it answers 409 for any other. Remove has
 * no such gate, deliberately: taking someone out of a closed or archived cohort is
 * cleanup and stays possible, so the two pickers offer different sets on purpose.
 */
const ACTIVE_COHORT_STATUS = 'active';

export const isAddableCohort = (cohort: { status: string | null }): boolean =>
  cohort.status === ACTIVE_COHORT_STATUS;

/** The one status the revoke door accepts; the server is still the authority on a stale read. */
export const ACTIVE_CODE_STATUS = 'active';

export const isRevocable = (code: AdminCode): boolean => code.status === ACTIVE_CODE_STATUS;

export const decodeErrorEnvelope = (error: unknown): ErrorEnvelope | null => {
  const body = asRecord((error as { data?: unknown } | null | undefined)?.data);
  const code = body?.[ERROR_ENVELOPE.code];

  if (typeof code !== 'string') return null;

  return { code, prose: blankToNull(body?.[ERROR_ENVELOPE.prose]) };
};

export const encodeCreateCohort = ({
  name,
  startDate,
  endDate,
  code = null,
  coachUserIds = [],
}: CreateCohortArgs): Record<string, string | number[]> => {
  const body: Record<string, string | number[]> = { [REQUEST_BODY.cohortName]: name.trim() };

  if (startDate) body[REQUEST_BODY.startDate] = startDate;
  if (endDate) body[REQUEST_BODY.endDate] = endDate;
  if (code !== null) body[REQUEST_BODY.code] = code;
  if (coachUserIds.length > 0) body[REQUEST_BODY.coachUserIds] = coachUserIds;

  return body;
};

export const encodeRenameCohort = ({ name }: RenameCohortArgs): Record<string, string> => ({
  [REQUEST_BODY.cohortName]: name.trim(),
});

export const encodeAttachCohortCoach = ({
  coachUserId,
}: CohortCoachArgs): Record<string, number> => ({ [REQUEST_BODY.coachUserId]: coachUserId });

/**
 * The rename answer, or null when the body is not a cohort. Decoded into its own shape
 * rather than through `decodeCohort`: the door answers with the cohort's own fields and
 * no aggregates, so decoding it as a listing row would mint zeroed counts.
 */
export const decodeRenamedCohort = (value: unknown): RenamedCohort | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return { id, name: blankToNull(row.name) };
};

/** An unpinned mint sends no cohort key at all rather than a null one. */
export const encodeMintCode = ({
  cohortId,
  code = null,
}: MintCodeArgs): Record<string, number | string> => ({
  ...(cohortId === null ? {} : { [REQUEST_BODY.cohortId]: cohortId }),
  ...(code === null ? {} : { [REQUEST_BODY.code]: code }),
});

/**
 * A minted code, or null when the body is not a code row. The response is the
 * code row plus `recycled`, decoded off the one object. `recycled` is always
 * sent; a missing one reads as a fresh mint, which overstates the insert rather
 * than claiming a recycle that never happened. A row carrying no value is
 * undecodable rather than a code with none: a mint exists to produce one.
 */
export const decodeMintedCode = (value: unknown): MintedCode | null => {
  const code = decodeCode(value);

  if (code === null || code.value === null) return null;

  return {
    code: { ...code, value: code.value },
    recycled: bool(asRecord(value)?.[RECYCLED_FIELD]),
  };
};

/** The move door raises this rather than guessing which allocation line a seat draws from. */
export const SOURCE_GRANT_REQUIRED = 'source_grant_required';

/**
 * Refusals the seat doors raise that a surface renders as the registry writes them.
 * Named so a cell can pin the word, not the sentence — the prose is the server's to
 * change, the word is contract.
 */
export const SEAT_REFUSAL = {
  privilegedKindMove: 'privileged_kind_move',
  privilegedKindRevoke: 'privileged_kind_revoke',
  privilegedKindCohort: 'privileged_kind_cohort',
  rosterCapacityExhausted: 'roster_capacity_exhausted',
  userAlreadyHasLiveSeat: 'user_already_has_live_seat',
  destinationIsOrigin: 'destination_is_origin',
  pendingInviteExists: 'pending_invite_exists',
  rosterSeatNotFound: 'roster_seat_not_found',
  rosterSeatNotActive: 'roster_seat_not_active',
  rosterSeatNotLive: 'roster_seat_not_live',
  /** The move failed for a reason this surface does not name. Not a refusal — a fault. */
  moveFailed: 'move_failed',
  /** The batch stopped before reaching this seat, so nothing was tried for it. */
  notAttempted: 'not_attempted',
  sourceGrantRequired: SOURCE_GRANT_REQUIRED,
  batchTooLarge: 'batch_too_large',
} as const;

/** Per-item outcomes the membership doors report that are not the landing word. */
export const MEMBERSHIP_SKIP = {
  seatNotOnCohortRoster: 'seat_not_on_cohort_roster',
  membershipExists: 'membership_exists',
  membershipNotFound: 'membership_not_found',
  seatNotActive: 'seat_not_active',
} as const;

/** The one outcome word per batch door that means the item landed. Anything else is a skip. */
export const BATCH_LANDED = {
  add: 'added',
  remove: 'removed',
  move: 'moved',
} as const;

/**
 * How many ids one batch takes. Mirrored from the server, which stays the authority:
 * the client holds it only to withhold a door that would be refused whole, losing the
 * eligible items along with the surplus.
 */
export const BATCH_LIMIT = 500;

/**
 * Seats these doors can act on. Mirrored from the server, which stays the authority:
 * the client holds them only to withhold a door that would be refused, and to say why.
 */
const MOVABLE_OFFERING_KIND = 'student';

/** The two kinds a dashboard tab acts on. A seat door is bound to the tab's kind. */
export const SEAT_KIND = { student: 'student', coach: 'coach' } as const;

export type SeatKind = (typeof SEAT_KIND)[keyof typeof SEAT_KIND];

export const isSeatKind = (offeringKind: string | null, kind: SeatKind): boolean =>
  offeringKind === kind;
const LIVE_SEAT_STATUSES: readonly string[] = ['invited', 'active'];
const ACTIVE_SEAT_STATUS = 'active';

export const isLiveSeat = (status: string | null): boolean =>
  status !== null && LIVE_SEAT_STATUSES.includes(status);

export const isActiveSeat = (status: string | null): boolean => status === ACTIVE_SEAT_STATUS;

export const isMovableKind = (offeringKind: string | null): boolean =>
  offeringKind === MOVABLE_OFFERING_KIND;

/** Its own key names: the row describes an ATTACHMENT, not the cohort as a listing row. */
const attachedCohort = (value: unknown): NamedRef | null => {
  const row = asRecord(value);
  const id = rowId(row?.cohort_id);

  return id === null ? null : { id, name: blankToNull(row?.cohort_name) };
};

/**
 * Absent and empty are different answers: the field rides coach-kind seats only, so its
 * absence says "not a coach seat" while an empty list says "this coach seat authorizes
 * nothing". Collapsing them would let a seat whose field never arrived render as a coach
 * who detaches nothing.
 */
const decodeAuthorizedCohortCoaches = (value: unknown): NamedRef[] | null => {
  if (value === undefined || value === null) return null;

  const decoded = decodeList(value, attachedCohort);

  // A dropped row would render a detach set SMALLER than the cascade will patch — the
  // understating direction, which is the whole reason this set is read rather than
  // derived. Unreadable is the honest answer; a short list is a confident wrong one.
  // The batch decoders reject on a dropped row for the same reason.
  if (decoded === null || decoded.dropped > 0) return null;

  return decoded.rows;
};

export const decodeSeatOccupant = (value: unknown): SeatOccupant | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    firstName: blankToNull(row.first_name),
    lastName: blankToNull(row.last_name),
    email: blankToNull(row.email),
  };
};

export const decodeSeat = (value: unknown): RosterSeat | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    rosterId: rowId(row.roster_id),
    status: blankToNull(row.status),
    source: blankToNull(row.source),
    joinedAt: blankToNull(row.joined_at),
    leftAt: blankToNull(row.left_at),
    occupant: decodeSeatOccupant(row.occupant),
    reservedEmail: blankToNull(row.reserved_email),
    // A cohort that will not decode is dropped rather than taking the seat with it; the
    // consequence prose then names one fewer cohort than the write will actually end.
    cohorts: decodeList(row.cohorts, identifiedRef)?.rows ?? [],
    authorizedCohortCoaches: decodeAuthorizedCohortCoaches(row.authorized_cohort_coaches),
  };
};

const decodeCapacityLine = (value: unknown): CapacityLine | null => {
  const row = asRecord(value);
  const grantId = rowId(row?.grant_id);

  if (grantId === null) return null;

  return { grantId, label: blankToNull(row?.label) };
};

export const decodeMoveDestination = (value: unknown): MoveDestination | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    managingInstitution: namedRef(row.managing_institution),
    offering: {
      ...namedRef(row.offering),
      kind: blankToNull(asRecord(row.offering)?.kind),
    },
    // A line that will not decode is dropped, which can leave one line where there were
    // two. The submit then carries a choice the server did not offer and is refused —
    // louder than silently drawing from a line nobody picked.
    lines: decodeList(row.lines, decodeCapacityLine)?.rows ?? [],
  };
};

/** Nothing to choose between: one line needs no picker, and none offers no pick. */
export const needsLineChoice = (destination: MoveDestination): boolean =>
  destination.lines.length > 1;

/**
 * A group whose rosters all failed to decode is dropped whole: an operator heading with
 * nothing under it offers a choice that cannot be made.
 */
export const decodeMoveDestinationGroup = (value: unknown): MoveDestinationGroup | null => {
  const row = asRecord(value);

  if (row === null) return null;

  const rosters = decodeList(row.rosters, decodeMoveDestination)?.rows ?? [];

  if (rosters.length === 0) return null;

  return { operator: namedRef(row.operator), rosters };
};

export const decodeRevokedSeat = (value: unknown): RevokedSeat | null => {
  const row = asRecord(value);
  const id = rowId(row?.id);

  if (row === null || id === null) return null;

  return {
    id,
    rosterId: rowId(row.roster_id),
    status: blankToNull(row.status),
    revokedCohortMemberships: num(row.revoked_cohort_memberships),
    detachedCohortCoaches: num(row.detached_cohort_coaches),
  };
};

export const decodeMovedSeat = (value: unknown): MovedSeat | null => {
  const row = asRecord(value);
  const seatId = rowId(asRecord(row?.seat)?.id);

  if (row === null || seatId === null) return null;

  return {
    seatId,
    fromRosterId: rowId(row.from_roster_id),
    fromSeatId: rowId(row.from_seat_id),
    rosterId: rowId(row.roster_id),
    sourceGrantId: rowId(row.source_grant_id),
    isOverCap: bool(row.over_cap),
    revokedCohortMemberships: num(row.revoked_cohort_memberships),
  };
};

/**
 * A result row missing either half is undecodable rather than defaulted: an unattributed
 * outcome cannot be shown against the seat it belongs to, and a seat with no outcome
 * would have to be reported as one thing or the other with nothing behind the choice.
 */
export const decodeMembershipResult = (value: unknown): BatchSeatResult | null => {
  const row = asRecord(value);
  const rosterSeatId = rowId(row?.roster_seat_id);
  const outcome = blankToNull(row?.outcome);

  if (rosterSeatId === null || outcome === null) return null;

  return { rosterSeatId, outcome };
};

/** The move door reports against `seat_id`; the membership doors against `roster_seat_id`. */
export const decodeBatchMoveResult = (value: unknown): BatchMoveResult | null => {
  const row = asRecord(value);
  const seatId = rowId(row?.seat_id);
  const outcome = blankToNull(row?.outcome);

  if (seatId === null || outcome === null) return null;

  return {
    seatId,
    outcome,
    isOverCap: bool(row?.over_cap),
    toSeatId: rowId(row?.to_seat_id),
    revokedCohortMemberships: nullableNum(row?.revoked_cohort_memberships),
  };
};

export const encodeMoveSeat = ({
  destinationRosterId,
  sourceGrantId,
}: MoveSeatArgs): Record<string, number> => ({
  [REQUEST_BODY.destinationRosterId]: destinationRosterId,
  ...(sourceGrantId === null ? null : { [REQUEST_BODY.sourceGrantId]: sourceGrantId }),
});

export const encodeCohortMemberships = ({
  rosterSeatIds,
}: AddCohortMembershipsArgs): Record<string, number[]> => ({
  [REQUEST_BODY.rosterSeatIds]: [...rosterSeatIds],
});

export const encodeBatchMoveSeats = ({
  seatIds,
  destinationRosterId,
  sourceGrantId,
}: BatchMoveSeatsArgs): Record<string, number | number[]> => ({
  [REQUEST_BODY.seatIds]: [...seatIds],
  [REQUEST_BODY.destinationRosterId]: destinationRosterId,
  ...(sourceGrantId === null ? null : { [REQUEST_BODY.sourceGrantId]: sourceGrantId }),
});
